import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listArtifacts, type Artifact, type ArtifactStatus, type ArtifactType } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface LinkedArtifactPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  codebaseId: string;
  excludeArtifactId: string;
  filter?: { type?: ArtifactType; status?: ArtifactStatus };
  title: string;
  description: string;
  confirmText: string;
  onPick: (artifactId: string) => void | Promise<void>;
  pending: boolean;
  errorMessage?: string | null;
}

export function LinkedArtifactPicker({
  open,
  onOpenChange,
  codebaseId,
  excludeArtifactId,
  filter,
  title,
  description,
  confirmText,
  onPick,
  pending,
  errorMessage,
}: LinkedArtifactPickerProps): React.ReactElement {
  const [pickedId, setPickedId] = useState('');
  const [search, setSearch] = useState('');

  const candidatesQuery = useQuery<Artifact[]>({
    queryKey: ['artifacts-picker', codebaseId, filter, search],
    queryFn: async () => {
      const res = await listArtifacts(codebaseId, {
        type: filter?.type,
        status: filter?.status,
        search: search.trim() === '' ? undefined : search.trim(),
        limit: 50,
      });
      return res.artifacts;
    },
    enabled: open,
  });

  const candidates = (candidatesQuery.data ?? []).filter(
    candidate => candidate.id !== excludeArtifactId
  );

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next) {
          setPickedId('');
          setSearch('');
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 pt-2">
          <input
            type="search"
            value={search}
            onChange={event => {
              setSearch(event.target.value);
            }}
            placeholder="Search by title, summary, slug…"
            className="rounded-md border border-border bg-surface px-2 py-1 text-sm"
            aria-label="Search candidates"
          />
          {candidatesQuery.isLoading && (
            <span className="text-xs text-text-secondary">Loading candidates…</span>
          )}
          {candidatesQuery.isError && (
            <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
              {candidatesQuery.error.message}
            </div>
          )}
          {candidates.length === 0 && !candidatesQuery.isLoading && (
            <span className="text-xs text-text-secondary">No matching artifacts.</span>
          )}
          {candidates.length > 0 && (
            <ul className="flex max-h-64 flex-col gap-1 overflow-auto rounded-md border border-border p-1">
              {candidates.map(candidate => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    onClick={(): void => {
                      setPickedId(candidate.id);
                    }}
                    className={`flex w-full items-center gap-2 rounded-md p-2 text-left text-sm hover:bg-surface ${
                      pickedId === candidate.id ? 'bg-surface/80 ring-1 ring-primary' : ''
                    }`}
                  >
                    <span className="rounded-md bg-surface-elevated px-2 py-0.5 font-mono text-xs text-text-secondary">
                      {candidate.type}
                    </span>
                    <span className="rounded-md bg-surface-elevated px-2 py-0.5 font-mono text-xs text-text-secondary">
                      {candidate.status}
                    </span>
                    <span className="flex-1 text-text-primary">{candidate.title}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {errorMessage && (
            <div role="alert" className="rounded-md bg-error/15 p-2 text-xs text-error">
              {errorMessage}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              disabled={pending}
              onClick={(): void => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={!pickedId || pending}
              onClick={(): void => {
                void onPick(pickedId);
              }}
            >
              {pending ? 'Working…' : confirmText}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
